// 'use client' is load-bearing: this section is a heavy interactive island and is
// registered as a STATIC import in components/RenderSections.js (never
// next/dynamic — see the #130 note there: a lazy boundary inside the streaming
// client island reorders the footer and breaks hydration).
'use client'

import React, { useEffect, useMemo, useState } from 'react'
import Img from '../../Img'
import { price } from '../../../utils/formats'
import { getShopItemPrice, getShopItemOriginalPrice } from '../../../utils/shopItemsAndCart'
import { isOutOfStock, getOutOfStockMessage } from '../../../utils/shopItems'
import { useRegionOffers } from '../../RegionOffers'
import { useRegionCountryCode } from '../../RegionGeo'
import { useRegionSubscriptions } from '../../RegionSurfaces'
import { useOffersStore } from '../../../store/offersStore'
import styles from './styles.module.css'
import {
  RIG_TYPES,
  SEAT_TYPES,
  MOUNT_TYPES,
  FREE_STANDING_TYPES,
  RIG_ACCESSORY_TYPES,
  WHEELBASE_TYPES,
  MOUNT_KIT_TYPES,
  PEDAL_TYPES,
  PEDAL_EXTRA_TYPES,
  WHEEL_TYPES,
  ACCESSORY_TYPES,
  MOUNT_SCREEN_PROFILE,
  SCREEN_PROFILE_COPY,
  SCREEN_OPTIONS,
  PC_OPTIONS,
  KEYBOARD_OPTIONS,
  THIRD_PARTY_SOURCE,
  thirdPartyCents,
  baseFamily,
  BASE_FAMILY_LABEL,
  BASE_SPECIFIC_ACCESSORIES,
  ASSEMBLY_OPTIONS,
  SHIPPING_OPTIONS,
  SUBSCRIPTION_ORDER,
  SUBSCRIPTION_BLURB,
  BUDGET_BANDS,
  SCREEN_LAYOUTS,
  EXTRA_WHEEL_ADAPTER,
  QUANTITY_TYPES,
  REQUIREMENT_ALIASES,
  IGNORE_OPTIONAL_FOR,
  THIRD_PARTY_FX,
} from './catalog'
import Guided, { QUESTIONS } from './Guided'
import { readSavedBuild, writeSavedBuild, clearSavedBuild } from './persist'
import { solveBuild } from './solve'

// Sentinel for "I don't want anything from this step". Stored in state like any
// other option id so an explicit skip is distinguishable from "not touched yet"
// (undefined → the step's computed default).
const NONE = '__none__'

const VRS_STEPS = [
  { key: 'rig', types: RIG_TYPES },
  { key: 'seat', types: SEAT_TYPES },
  { key: 'mount', types: MOUNT_TYPES },
  { key: 'wheelbase', types: WHEELBASE_TYPES },
  { key: 'mountKit', types: MOUNT_KIT_TYPES },
  { key: 'pedals', types: PEDAL_TYPES },
  { key: 'wheel', types: WHEEL_TYPES },
]

// An offer with no `price` is not sold in this region (the backend returns the
// row with a null price). Drop it rather than rendering a 0 card — a legitimate
// EUR 0 line (the GT-2 mount kits) still has a price object with priceCents 0.
const pickTypes = (byType, types) =>
  types.map((t) => byType.get(t)).filter((o) => o && o.price && typeof o.price.priceCents === 'number')

// The 6Nm uDFP20 is the only torque tier the backend does not name with its Nm
// figure ('Upgradable Torque uDFP20 Wheel Base'), which reads as if it were a
// different product from its 9/12/15Nm siblings. Append the tier from the
// itemType so the whole ladder is labelled consistently.
const offerName = (o) => {
  const base = o.namePublicShop || o.name || (o.itemType && o.itemType.itemTypeName) || o.type
  const tier = /^WHEEL_BASE_UPGRADABLE_TORQUE_(\d+)$/.exec(o.type || '')
  if (tier && !new RegExp(`${tier[1]}\\s*Nm`, 'i').test(base)) return `${base} ${tier[1]}Nm`
  return base
}

// Prices follow the shop exactly: the effective price is discountPrice ?? price,
// the struck-through original is regularPrice ?? price, and a strike only renders
// when the backend actually discounted the line.
const offerCents = (o) => {
  const p = o && getShopItemPrice(o)
  return p ? p.priceCents : 0
}
const offerOriginalCents = (o) => {
  const p = o && getShopItemOriginalPrice(o)
  return p ? p.priceCents : 0
}
const isDiscounted = (o) => !!o && offerOriginalCents(o) > offerCents(o)

const offerImage = (o) => (o && ((o.galleryImages && o.galleryImages[0]) || o.imageUrl)) || null
const bundledTypes = (o) => (o && Array.isArray(o.bundledItemTypes) ? o.bundledItemTypes : [])

// The VESA pattern is only in the itemType, and two mounts (both TX43 variants)
// share ONE public name — without this they render as identical cards.
const vesaOf = (type) => (/200x200/.test(type) ? 'VESA 200x200' : /100x100/.test(type) ? 'VESA 100x100' : null)

// Sub-label under an option name: the colour for a rig, the VESA pattern for a mount.
const offerMeta = (o) => (o.colorInfo && o.colorInfo.colorName) || vesaOf(o.type || '')

// The GT-2 colour photography ships as matched `<colour>-seat-N` / `-noseat-N`
// pairs (the same pairs /product/gt2-cockpit swaps through
// getConfigurationImages), so the seatless view is one string swap away.
const swapSeat = (image, withSeat) => {
  if (!image || !image.src) return image
  const from = withSeat ? '-noseat-' : '-seat-'
  const to = withSeat ? '-seat-' : '-noseat-'
  if (image.src.indexOf(from) === -1) return image
  const swap = (v) => (typeof v === 'string' ? v.split(from).join(to) : v)
  return { ...image, src: swap(image.src), srcSet: swap(image.srcSet), placeholderUrl: swap(image.placeholderUrl) }
}

// The Black rig's own offer images are studio shots from a different angle to the
// six colours, which makes the colour row look inconsistent. Its matching
// `black-seat-N` / `black-noseat-N` files DO exist in the bucket, so point Black
// at those and every swatch shows the same view.
const BLACK_RIG_IMAGE = {
  src: 'https://storage.googleapis.com/vrs-media/assets/images/gt2-cockpit/black-seat-0.webp',
  alt: 'VRS DirectForce GT-2 Cockpit Black',
  width: 1463,
  height: 1463,
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path d="M6.2 11.6 2.9 8.3l1.1-1.1 2.2 2.2 5.8-5.8 1.1 1.1z" fill="currentColor" />
    </svg>
  )
}

// Declared at module scope on purpose: a component defined inside the render
// body gets a new identity every render, which remounts the whole step subtree
// on each click.
function Step({ n, title, caption, children }) {
  return (
    <section className={styles.step}>
      <header className={styles.stepHead}>
        <span className={styles.stepNum}>{n}</span>
        <div>
          <h2 className={styles.stepTitle}>{title}</h2>
          {caption && <p className={styles.stepCaption}>{caption}</p>}
        </div>
      </header>
      {children}
    </section>
  )
}

function TurnkeyConfigurator({
  title = 'Build your turnkey simulator',
  intro = '',
  features = [],
}) {
  const { offers } = useRegionOffers()
  const ensureFullOffers = useOffersStore((s) => s.ensureFullOffers)
  // The country THIS page priced against. Passing it is load-bearing: with no
  // argument the store falls back to the region store, and with an explicit null
  // it geo-IP-resolves — which on /eu silently swaps in the US catalog (rigs are
  // EU-only, so every rig card would fall back to a 0 price).
  const pricingCountry = useRegionCountryCode()
  const { subscriptions } = useRegionSubscriptions()

  useEffect(() => {
    ensureFullOffers(pricingCountry)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [chosen, setChosen] = useState({})
  const [multi, setMulti] = useState({ rigAccessories: [], pedalExtras: [], accessories: [] })
  // Per-itemType counts for the handful of accessories a build can hold more
  // than one of (a quick-release adapter per extra steering wheel). Absent = 1.
  const [quantities, setQuantities] = useState({})
  // null until the visitor picks a lane. 'guided' runs the question flow,
  // 'expert' drops straight into the full configurator.
  const [mode, setMode] = useState(null)
  const [question, setQuestion] = useState(QUESTIONS[0])
  const [answers, setAnswers] = useState({ extraWheels: 0 })
  const [submitState, setSubmitState] = useState({ status: 'idle' })

  // Restore a saved build POST-MOUNT only. Reading the cookie during render would
  // make the client's first render differ from the SSR HTML (which is shared by
  // every visitor and cached by pathname), i.e. a hydration mismatch — the same
  // reason the region cookie is only ever overlaid after mount (INSTRUCTIONS §3).
  useEffect(() => {
    const saved = readSavedBuild()
    if (!saved) return
    if (saved.chosen) setChosen(saved.chosen)
    if (saved.multi) setMulti(saved.multi)
    if (saved.quantities) setQuantities(saved.quantities)
    if (saved.answers) setAnswers(saved.answers)
    if (saved.question) setQuestion(saved.question)
    if (saved.mode) setMode(saved.mode)
  }, [])

  // Persist on every change once a lane has been chosen. Skipping mode===null
  // keeps an untouched visit from writing a cookie at all.
  useEffect(() => {
    if (mode === null) return
    writeSavedBuild({ mode, question, answers, chosen, multi, quantities })
  }, [mode, question, answers, chosen, multi, quantities])

  const startOver = () => {
    clearSavedBuild()
    setChosen({})
    setMulti({ rigAccessories: [], pedalExtras: [], accessories: [] })
    setQuantities({})
    setAnswers({ extraWheels: 0 })
    setQuestion(QUESTIONS[0])
    setSubmitState({ status: 'idle' })
    setMode(null)
  }

  const byType = useMemo(() => {
    const map = new Map()
    ;(offers || []).forEach((o) => {
      if (o && o.type && !map.has(o.type)) map.set(o.type, o)
    })
    return map
  }, [offers])

  const currency = useMemo(() => {
    const withPrice = (offers || []).find((o) => o && o.price && o.price.currency)
    return withPrice ? withPrice.price.currency : 'EUR'
  }, [offers])

  const options = useMemo(() => {
    const out = {}
    VRS_STEPS.forEach((s) => {
      out[s.key] = pickTypes(byType, s.types)
    })
    return out
  }, [byType])

  // A sold-out option can never be the resolved default, and cannot be picked at
  // all (the card is disabled below) — so a build is always orderable as shown.
  const resolveStep = (key) => {
    const list = options[key] || []
    const picked = chosen[key]
    if (picked === NONE) return NONE
    if (picked && list.some((o) => o.type === picked && !isOutOfStock(o))) return picked
    const firstInStock = list.find((o) => !isOutOfStock(o))
    return firstInStock ? firstInStock.type : NONE
  }

  const sel = {
    rig: resolveStep('rig'),
    seat: resolveStep('seat'),
    mount: resolveStep('mount'),
    wheelbase: resolveStep('wheelbase'),
    mountKit: resolveStep('mountKit'),
    pedals: resolveStep('pedals'),
    wheel: resolveStep('wheel'),
  }

  const hasSeat = sel.seat !== NONE

  // --- compatibility ---------------------------------------------------------
  // Every itemType currently in the build, with bundles expanded, so an item the
  // 3-Pedal Set already contains counts as present.
  const selectedTypes = useMemo(() => {
    const set = new Set()
    const add = (t) => {
      if (!t || t === NONE) return
      set.add(t)
      bundledTypes(byType.get(t)).forEach((b) => set.add(b))
    }
    Object.values(sel).forEach(add)
    Object.values(multi).forEach((list) => (list || []).forEach(add))
    return set
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byType, sel.rig, sel.seat, sel.mount, sel.wheelbase, sel.mountKit, sel.pedals, sel.wheel, multi])

  const selectedBase = sel.wheelbase !== NONE ? byType.get(sel.wheelbase) : null
  const selectedBaseFamily = selectedBase ? baseFamily(selectedBase.type) : null

  /**
   * Why an option cannot be picked right now, or null when it can.
   * Order matters: stock first (a sold-out item is never orderable), then the
   * wheel-base family, then "already in the bundle", then the backend's own
   * `optionalFor` prerequisite.
   */
  const blockReason = (o, { addOn = false } = {}) => {
    if (!o) return null
    if (isOutOfStock(o)) return getOutOfStockMessage(o) || 'Out of stock'

    const fits = BASE_SPECIFIC_ACCESSORIES[o.type]
    if (fits && selectedBaseFamily && fits.indexOf(selectedBaseFamily) === -1) {
      return `Not compatible with ${selectedBase ? offerName(selectedBase) : BASE_FAMILY_LABEL[selectedBaseFamily]}`
    }

    // ADD-ONS ONLY. Inside a single-select step the bundle and its parts are
    // ALTERNATIVES (3-Pedal Set vs Throttle & Brake), so 'already in the bundle'
    // must not disable the sibling you are choosing between.
    if (addOn) {
      const container = Object.values(sel)
        .map((t) => (t !== NONE ? byType.get(t) : null))
        .find((c) => c && bundledTypes(c).indexOf(o.type) !== -1)
      if (container) return `Already in the ${offerName(container)}`
    }

    // The backend's own prerequisite, but only when the required itemType is a
    // real catalog entry. Several accessories still carry the retired umbrella
    // type `WHEEL_BASE` in optionalFor, which nothing can ever satisfy — those
    // are governed by the wheel-base family rule above instead.
    const req = IGNORE_OPTIONAL_FOR.has(o.type) ? null : o.optionalFor
    if (req && req.itemType && byType.has(req.itemType)) {
      const accepted = REQUIREMENT_ALIASES[req.itemType] || [req.itemType]
      if (!accepted.some((t) => selectedTypes.has(t))) {
        return `Requires the ${req.itemTypeName || req.itemType}`
      }
    }
    return null
  }

  // --- screens ---------------------------------------------------------------
  const screenProfile = sel.mount === NONE ? null : MOUNT_SCREEN_PROFILE[sel.mount] || null
  const screenOptions = screenProfile ? SCREEN_OPTIONS[screenProfile] || [] : []
  const screenId = (() => {
    const picked = chosen.screen
    if (picked === NONE) return NONE
    if (picked && screenOptions.some((s) => s.id === picked)) return picked
    return screenOptions.length ? screenOptions[0].id : NONE
  })()
  const screen = screenOptions.find((s) => s.id === screenId) || null

  const pcId = chosen.pc === undefined ? PC_OPTIONS[1].id : chosen.pc
  const pc = PC_OPTIONS.find((p) => p.id === pcId) || null
  const keyboardId = chosen.keyboard === undefined ? KEYBOARD_OPTIONS[0].id : chosen.keyboard
  const keyboard = KEYBOARD_OPTIONS.find((k) => k.id === keyboardId) || null
  const shippingId = chosen.shipping === undefined ? SHIPPING_OPTIONS[0].id : chosen.shipping
  const shipping = SHIPPING_OPTIONS.find((x) => x.id === shippingId) || null
  const assemblyId = chosen.assembly === undefined ? ASSEMBLY_OPTIONS[0].id : chosen.assembly
  const assembly = ASSEMBLY_OPTIONS.find((x) => x.id === assemblyId) || null

  const plans = useMemo(() => {
    const bySlug = new Map((subscriptions || []).map((s) => [s.code, s]))
    return SUBSCRIPTION_ORDER.map((c) => bySlug.get(c)).filter(Boolean)
  }, [subscriptions])
  const planCode = chosen.plan === undefined ? 'casual' : chosen.plan
  const plan = plans.find((p) => p.code === planCode) || null
  const planMonthlyCents = plan && plan.price && plan.price.monthly ? plan.price.monthly.priceCents : 0

  const setStep = (key, id) => setChosen((prev) => ({ ...prev, [key]: id }))
  const toggleMulti = (key, type) =>
    setMulti((prev) => {
      const list = prev[key] || []
      return { ...prev, [key]: list.includes(type) ? list.filter((t) => t !== type) : [...list, type] }
    })

  // --- build sheet + total ---------------------------------------------------
  const lines = useMemo(() => {
    const rows = []
    const pushOffer = (label, type) => {
      if (!type || type === NONE) return
      const o = byType.get(type)
      if (!o) return
      const qty = quantities[type] > 1 ? quantities[type] : 1
      rows.push({
        id: type,
        label,
        name: qty > 1 ? `${offerName(o)} x ${qty}` : offerName(o),
        cents: offerCents(o) * qty,
        wasCents: isDiscounted(o) ? offerOriginalCents(o) * qty : null,
      })
    }
    // A multi-select the user ticked earlier can become incompatible after a
    // later change (swap the base and its adapter no longer fits). Those drop out
    // of the sheet and the total rather than silently pricing an unbuildable rig.
    const pushChecked = (label, type) => {
      const o = byType.get(type)
      if (!o || blockReason(o, { addOn: true })) return
      pushOffer(label, type)
    }

    pushOffer('Rig', sel.rig)
    pushOffer('Seat', sel.seat)
    pushOffer('Monitor mount', sel.mount)
    ;(multi.rigAccessories || []).forEach((t) => pushChecked('Rig accessory', t))
    pushOffer('Wheel base', sel.wheelbase)
    pushOffer('Base mount', sel.mountKit)
    pushOffer('Pedals', sel.pedals)
    ;(multi.pedalExtras || []).forEach((t) => pushChecked('Pedal option', t))
    pushOffer('Steering wheel', sel.wheel)
    ;(multi.accessories || []).forEach((t) => pushChecked('Accessory', t))

    if (pc) rows.push({ id: pc.id, label: 'PC', name: pc.name, cents: thirdPartyCents(pc.priceEur, currency), estimated: true })
    if (screen) {
      rows.push({
        id: screen.id,
        label: screen.qty > 1 ? `Screens (${screen.qty}x)` : 'Screen',
        name: screen.name,
        cents: thirdPartyCents(screen.priceEur, currency) * screen.qty,
        estimated: true,
      })
    }
    if (keyboard) {
      rows.push({ id: keyboard.id, label: 'Keyboard', name: keyboard.name, cents: thirdPartyCents(keyboard.priceEur, currency), estimated: true })
    }
    if (shipping) {
      rows.push({ id: shipping.id, label: 'Shipping', name: shipping.name, cents: thirdPartyCents(shipping.priceEur, currency), placeholder: shipping.placeholder })
    }
    if (assembly) {
      rows.push({ id: assembly.id, label: 'Assembly', name: assembly.name, cents: thirdPartyCents(assembly.priceEur, currency), placeholder: assembly.placeholder })
    }
    return rows
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byType, sel.rig, sel.seat, sel.mount, sel.wheelbase, sel.mountKit, sel.pedals, sel.wheel, multi, quantities, pc, screen, keyboard, shipping, assembly, currency, selectedTypes, selectedBaseFamily])

  const total = lines.reduce((sum, l) => sum + (l.cents || 0), 0)
  const gearIds = new Set([pc && pc.id, screen && screen.id, keyboard && keyboard.id].filter(Boolean))
  const gearTotal = lines.filter((l) => gearIds.has(l.id)).reduce((s, l) => s + l.cents, 0)
  const serviceTotal = lines.filter((l) => l.label === 'Shipping' || l.label === 'Assembly').reduce((s, l) => s + l.cents, 0)
  const vrsTotal = total - gearTotal - serviceTotal

  // The preview mirrors the seat choice: seatless photography when the customer
  // brings their own bucket.
  const rigCardImage = (o) => {
    const img = o.type === 'GT2_COCKPIT_BLACK' ? BLACK_RIG_IMAGE : offerImage(o)
    return swapSeat(img, hasSeat)
  }

  const heroOffer = byType.get(sel.rig === NONE ? RIG_TYPES[0] : sel.rig)
  const heroImage = heroOffer ? rigCardImage(heroOffer) : null

  // --- guided flow -----------------------------------------------------------
  // Every answer is written straight into the SAME state the expert view uses, so
  // the build panel is live beside the questions and the rider can bail into the
  // full configurator at any point with everything already applied.
  // Budget bands are written in EUR; render them in the page currency with the
  // same FX table the third-party prices use.
  const formatEur = (amountEur) => price(Math.round(amountEur * (THIRD_PARTY_FX[currency] || 1)) * 100, currency)

  const bandLabel = (b) => {
    if (!b.minEur) return `Up to ${formatEur(b.maxEur)}`
    if (b.maxEur >= 25000) return `Over ${formatEur(b.minEur)}`
    return `${formatEur(b.minEur)} to ${formatEur(b.maxEur)}`
  }

  const applyAnswers = (next) => {
    const layout = SCREEN_LAYOUTS.find((l) => l.id === next.layout) || null
    const sizeEntry = layout && (layout.sizes.find((x) => x.size === next.screenSize) || layout.sizes[0])
    const mountType = sizeEntry ? sizeEntry.mount : null
    const screenProfile = mountType ? MOUNT_SCREEN_PROFILE[mountType] : null
    const band = BUDGET_BANDS.find((b) => b.id === next.budget)

    const solverInput = {
      age: next.age,
      budgetEur: band ? band.maxEur : 0,
      seat: next.seat !== false,
      mountType,
      screenProfile,
      screenSize: next.screenSize,
      extraWheels: 0,
      adapterType: null,
    }
    const rigType = next.rig || (options.rig[0] && options.rig[0].type)
    const mountKitType = (options.mountKit[0] && options.mountKit[0].type) || null

    // The wheel-side adapter fits every base, so it is a fixed cost the solver can
    // account for in a single pass.
    const adapterType = next.extraWheels > 0 && byType.has(EXTRA_WHEEL_ADAPTER) ? EXTRA_WHEEL_ADAPTER : null
    const solved = solveBuild({
      byType,
      answers: { ...solverInput, extraWheels: adapterType ? next.extraWheels : 0, adapterType },
      currency,
      rigType,
      mountKitType,
    })

    // A yes/no answer overrides the solver for that one part; everything the rider
    // has not been asked about yet stays with whatever the budget picked.
    const clutchSet = next.clutch === true ? 'PEDALS_THROTTLE_AND_BRAKE_CLUTCH_PEDAL' : next.clutch === false ? 'PEDALS_THROTTLE_AND_BRAKE' : null
    const ascher = 'VRS_ASCHER_ULTIMATE_WHEEL_QR_CONNECT_WHEEL_ADAPTER'
    const wheelPick = next.screenWheel === true && byType.has(ascher) ? ascher : solved.wheel

    setChosen((prev) => ({
      ...prev,
      rig: rigType,
      seat: next.seat === false ? NONE : 'GT2_SEAT',
      mount: mountType || prev.mount,
      wheelbase: solved.wheelbase || NONE,
      pedals: clutchSet && byType.has(clutchSet) ? clutchSet : solved.pedals || NONE,
      wheel: wheelPick || NONE,
      pc: solved.pc,
      screen: next.screenId || solved.screen || NONE,
      assembly: next.assembly || solved.assembly,
      shipping: next.shipping || prev.shipping,
      plan: next.plan || prev.plan,
    }))

    // The three rig accessories are plain in/out toggles.
    const toggles = [
      ['shifter', 'SHIFTER_MOUNT'],
      ['keyboardTray', 'KEYBOARD_MOUNT'],
      ['pcTray', 'PC_TRAY'],
    ]
    setMulti((prev) => {
      let list = prev.rigAccessories
      toggles.forEach(([key, type]) => {
        if (next[key] === true && !list.includes(type)) list = [...list, type]
        if (next[key] === false && list.includes(type)) list = list.filter((t) => t !== type)
      })
      return list === prev.rigAccessories ? prev : { ...prev, rigAccessories: list }
    })

    if (next.extraWheels > 0 && adapterType) {
      setMulti((prev) => ({
        ...prev,
        accessories: prev.accessories.includes(adapterType) ? prev.accessories : [...prev.accessories, adapterType],
      }))
      setQuantities((prev) => ({ ...prev, [adapterType]: next.extraWheels }))
    }
  }

  // The screen-wheel question is only worth asking when the budget could actually
  // absorb the upgrade, so it is skipped in BOTH directions rather than shown and
  // then refused.
  const skipQuestion = (q, next) => {
    if (q !== 'screenWheel') return false
    const band = BUDGET_BANDS.find((b) => b.id === next.budget)
    if (!band) return true
    const ascher = byType.get('VRS_ASCHER_ULTIMATE_WHEEL_QR_CONNECT_WHEEL_ADAPTER')
    const current = byType.get(sel.wheel)
    if (!ascher || isOutOfStock(ascher)) return true
    const delta = offerCents(ascher) - (current ? offerCents(current) : 0)
    return total + delta > Math.round(band.maxEur * (THIRD_PARTY_FX[currency] || 1)) * 100
  }

  const step = (from, dir, next) => {
    let i = QUESTIONS.indexOf(from) + dir
    while (i > 0 && i < QUESTIONS.length - 1 && skipQuestion(QUESTIONS[i], next)) i += dir
    return QUESTIONS[Math.max(0, Math.min(i, QUESTIONS.length - 1))]
  }

  const onAnswer = (key, value) => {
    // One click in the monitor step answers two things: the inch size (which
    // decides the stand) and the exact model the rider picked.
    const next =
      key === 'screenPick'
        ? { ...answers, screenSize: value.size, screenId: value.id }
        : { ...answers, [key]: value }
    setAnswers(next)
    applyAnswers(next)
    setQuestion(step(question, 1, next))
  }

  const onBack = () => setQuestion(step(question, -1, answers))

  const onSubmitOrder = async ({ email, phone }) => {
    setSubmitState({ status: 'sending' })
    try {
      const res = await fetch('/api/turnkeyOrder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          phone,
          name: answers.name || '',
          age: answers.age || null,
          currency,
          totalCents: total,
          lines: lines.map((l) => ({ label: l.label, name: l.name, cents: l.cents, wasCents: l.wasCents || null })),
          subscription: plan ? { code: plan.code, name: plan.name, monthlyCents: planMonthlyCents } : null,
        }),
      })
      if (!res.ok) throw new Error(`Request failed (${res.status})`)
      setSubmitState({ status: 'sent', email })
    } catch (e) {
      setSubmitState({ status: 'error', error: 'We could not send that. Please try again, or email dimitrios@vrs.racing.' })
    }
  }

  // --- render helpers --------------------------------------------------------
  const PriceTag = ({ cents, wasCents, suffix }) => (
    <span className={styles.cardPrice}>
      {wasCents ? <span className={styles.was}>{price(wasCents, currency)}</span> : null}
      <span className={wasCents ? styles.now : undefined}>{cents === 0 ? 'Included' : price(cents, currency)}</span>
      {suffix ? <span className={styles.est}>{suffix}</span> : null}
    </span>
  )

  const renderOfferCard = (o, { selected, onSelect, multiSelect, image }) => {
    const reason = blockReason(o, { addOn: !!multiSelect })
    const img = image !== undefined ? image : offerImage(o)
    return (
      <button
        type="button"
        key={o.type}
        disabled={!!reason}
        className={[styles.card, selected && !reason && styles.cardSelected, reason && styles.cardBlocked].filter(Boolean).join(' ')}
        onClick={reason ? undefined : onSelect}
        aria-pressed={selected && !reason}
        aria-disabled={!!reason}
        title={reason || undefined}
      >
        {img && (
          <span className={styles.cardMedia}>
            <Img image={img} sizes="220px" layout="fill" objectFit="contain" reserveSpace={false} alt={offerName(o)} />
          </span>
        )}
        <span className={styles.cardBody}>
          <span className={styles.cardName}>{offerName(o)}</span>
          {offerMeta(o) && <span className={styles.cardMeta}>{offerMeta(o)}</span>}
          {FREE_STANDING_TYPES.has(o.type) && <span className={styles.badge}>Free-standing</span>}
          {reason && <span className={styles.badgeBlocked}>{reason}</span>}
          <PriceTag cents={offerCents(o)} wasCents={isDiscounted(o) ? offerOriginalCents(o) : null} />
        </span>
        <span className={[styles.tick, multiSelect && styles.tickSquare].filter(Boolean).join(' ')} aria-hidden="true">
          {selected && !reason && <CheckIcon />}
        </span>
      </button>
    )
  }

  const renderPlainCard = ({ id, name, spec, cents, selected, onSelect, pill, note, suffix }) => (
    <button
      type="button"
      key={id}
      className={[styles.card, styles.cardPlain, selected && styles.cardSelected].filter(Boolean).join(' ')}
      onClick={onSelect}
      aria-pressed={selected}
    >
      <span className={styles.cardBody}>
        {pill && <span className={styles.sizePill}>{pill}</span>}
        <span className={styles.cardName}>{name}</span>
        {spec && <span className={styles.cardMeta}>{spec}</span>}
        <PriceTag cents={cents} suffix={suffix} />
        {note && <span className={styles.cardNote}>{note}</span>}
      </span>
      <span className={styles.tick} aria-hidden="true">{selected && <CheckIcon />}</span>
    </button>
  )

  const renderNoneCard = (selected, onSelect, label = 'None') =>
    renderPlainCard({ id: 'none', name: label, cents: 0, selected, onSelect })

  const singleStep = (key, { optional, noneLabel, imageFor } = {}) => {
    if (!(options[key] || []).length) return <p className={styles.scopeNote}>Not sold in your region.</p>
    return (
      <div className={styles.grid}>
        {(options[key] || []).map((o) =>
          renderOfferCard(o, {
            selected: sel[key] === o.type,
            onSelect: () => setStep(key, o.type),
            image: imageFor ? imageFor(o) : undefined,
          })
        )}
        {optional && renderNoneCard(sel[key] === NONE, () => setStep(key, NONE), noneLabel)}
      </div>
    )
  }

  const multiStep = (key, types) => {
    const list = pickTypes(byType, types)
    if (!list.length) return null
    return (
      <div className={styles.grid}>
        {list.map((o) =>
          renderOfferCard(o, {
            selected: (multi[key] || []).includes(o.type),
            onSelect: () => toggleMulti(key, o.type),
            multiSelect: true,
          })
        )}
      </div>
    )
  }

  const noOffers = !offers || !offers.length || !(options.rig || []).length

  return (
    <div className={styles.wrapper}>
      <div className={styles.inner}>
        <header className={styles.pageHead}>
          {mode !== null && (
            <button type="button" className={styles.startOver} onClick={startOver}>
              Start over
            </button>
          )}
          <span className={styles.eyebrow}>Turnkey simulator configurator</span>
          <h1 className={styles.pageTitle}>{title}</h1>
          {intro && <p className={styles.pageIntro}>{intro}</p>}
          {features && features.length > 0 && (
            <ul className={styles.features}>
              {features.map((f) => (
                <li key={f}>
                  <span className={styles.featureTick}><CheckIcon /></span>
                  {f}
                </li>
              ))}
            </ul>
          )}
        </header>

        {noOffers ? (
          <p className={styles.empty}>Sorry, our custom-built simulators are not available for your location right now.</p>
        ) : mode === null ? (
          <div className={styles.chooser}>
            <button type="button" className={styles.chooserCard} onClick={() => setMode('guided')}>
              <span className={styles.chooserKicker}>Start here</span>
              <span className={styles.chooserTitle}>I&apos;m new to sim racing</span>
              <span className={styles.chooserBlurb}>
                Answer a handful of questions and we&apos;ll build the rig for you, picking the parts your budget covers in
                the order that matters on track.
              </span>
              <span className={styles.chooserCta}>Answer a few questions</span>
            </button>
            <button type="button" className={styles.chooserCard} onClick={() => setMode('expert')}>
              <span className={styles.chooserKicker}>I know what I want</span>
              <span className={styles.chooserTitle}>I&apos;m experienced</span>
              <span className={styles.chooserBlurb}>
                Go straight to the full configurator. Every part, every option, live prices and stock, with the
                incompatible combinations locked out.
              </span>
              <span className={styles.chooserCta}>Open the configurator</span>
            </button>
          </div>
        ) : (
          <div className={styles.layout}>
            <div className={styles.steps}>
              {mode === 'guided' && (
                <Guided
                  question={question}
                  answers={answers}
                  rigOptions={options.rig}
                  rigImageFor={rigCardImage}
                  offerFor={(t) => byType.get(t)}
                  offerName={offerName}
                  offerPrice={(o) => price(offerCents(o), currency)}
                  mountAvailable={(t) => {
                    // A stand with no region price is not sold here (the TX43 went
                    // price-null mid-session), and a sold-out one cannot be ordered.
                    // Either way the guided flow must not offer that screen size.
                    const o = byType.get(t)
                    return !!o && !!o.price && !isOutOfStock(o)
                  }}
                  bandLabel={bandLabel}
                  formatEur={formatEur}
                  plans={plans}
                  planCode={planCode}
                  submitState={submitState}
                  onAnswer={onAnswer}
                  onBack={onBack}
                  onSubmitOrder={onSubmitOrder}
                />
              )}
              {mode === 'expert' && (<>
              <Step n={1} title="Start with the rig" caption="Every build starts on the VRS DirectForce GT-2 aluminium cockpit. Pick a colour.">
                {singleStep('rig', { imageFor: rigCardImage })}
              </Step>

              <Step n={2} title="Seat" caption="The GT-2 seat is fibreglass-shelled with a fixed-back racing shape. Bringing your own bucket is fine too, and the photos above update to match.">
                {singleStep('seat', { optional: true, noneLabel: "I'll use my own seat" })}
              </Step>

              <Step
                n={3}
                title="Monitor mount and rig accessories"
                caption="The mount you choose here decides which screens you can fit in step 9. The TX43 and TX60 are free-standing, so they sit behind the rig rather than bolting to it."
              >
                {singleStep('mount', { optional: true, noneLabel: 'No mount (I have my own)' })}
                <h3 className={styles.subTitle}>Rig accessories</h3>
                {multiStep('rigAccessories', RIG_ACCESSORY_TYPES)}
              </Step>

              <Step n={4} title="Wheel base" caption="uDFP20 bases unlock more torque later with a code, no hardware swap. DFP15 and the 20 Nm DFP20 ship at their rated torque. Your choice here decides which adapters and quick releases fit in step 7.">
                {singleStep('wheelbase', { optional: true, noneLabel: 'No wheel base' })}
                {(options.mountKit || []).length > 0 && sel.wheelbase !== NONE && (
                  <>
                    <h3 className={styles.subTitle}>Where the base mounts</h3>
                    <p className={styles.subCaption}>Both kits are included at no cost. Front is the standard position; bottom drops the base for a more reclined, formula-style driving position.</p>
                    {singleStep('mountKit')}
                  </>
                )}
              </Step>

              <Step n={5} title="Pedals" caption="VRS DirectForce Pro pedals, one of the most renowned pedal sets in sim racing. The 3-pedal set adds the clutch.">
                {singleStep('pedals', { optional: true, noneLabel: 'No pedals' })}
                {sel.pedals !== NONE && (
                  <>
                    <h3 className={styles.subTitle}>Pedal options</h3>
                    {multiStep('pedalExtras', PEDAL_EXTRA_TYPES)}
                  </>
                )}
              </Step>

              <Step n={6} title="Steering wheel" caption="All VRS wheels use the same quick release, so you can add more later.">
                {singleStep('wheel', { optional: true, noneLabel: 'No steering wheel' })}
              </Step>

              <Step n={7} title="Quick releases and accessories" caption="Which adapter you need depends on the wheel base above. Anything that does not fit your base is greyed out with the reason.">
                {multiStep('accessories', ACCESSORY_TYPES)}
              </Step>

              <Step n={8} title="PC" caption="Three prebuilt tiers. We install and configure the sim, the wheel base firmware and SimHub before the rig ships.">
                <div className={styles.grid}>
                  {PC_OPTIONS.map((p) => (
                    <button
                      type="button"
                      key={p.id}
                      className={[styles.card, styles.cardTall, pcId === p.id && styles.cardSelected].filter(Boolean).join(' ')}
                      onClick={() => setStep('pc', p.id)}
                      aria-pressed={pcId === p.id}
                    >
                      <span className={styles.cardBody}>
                        <span className={styles.tierRow}>
                          <span className={styles.tier}>{p.tier}</span>
                          {p.recommended && <span className={styles.badge}>Most picked</span>}
                        </span>
                        <span className={styles.cardName}>{p.name}</span>
                        <span className={styles.cardBlurb}>{p.blurb}</span>
                        <ul className={styles.specList}>
                          {p.specs.map((s) => <li key={s}>{s}</li>)}
                        </ul>
                        <PriceTag cents={thirdPartyCents(p.priceEur, currency)} suffix=" est." />
                      </span>
                      <span className={styles.tick} aria-hidden="true">{pcId === p.id && <CheckIcon />}</span>
                    </button>
                  ))}
                  {renderNoneCard(pcId === NONE, () => setStep('pc', NONE), 'No PC (I have one)')}
                </div>
              </Step>

              <Step
                n={9}
                title="Screens"
                caption={screenProfile ? SCREEN_PROFILE_COPY[screenProfile].caption : 'Pick a monitor mount in step 3 and the screens that fit it will show up here.'}
              >
                {screenProfile && (
                  <p className={styles.scopeNote}>
                    Showing <strong>{SCREEN_PROFILE_COPY[screenProfile].title}</strong> options for{' '}
                    <strong>{offerName(byType.get(sel.mount) || {})}</strong>.
                  </p>
                )}
                {screenOptions.length > 0 ? (
                  <div className={styles.grid}>
                    {screenOptions.map((s) =>
                      renderPlainCard({
                        id: s.id,
                        pill: s.size,
                        name: s.name,
                        spec: s.spec,
                        cents: thirdPartyCents(s.priceEur, currency),
                        suffix: s.qty > 1 ? ` each · ${price(thirdPartyCents(s.priceEur, currency) * s.qty, currency)} for ${s.qty}` : ' est.',
                        selected: screenId === s.id,
                        onSelect: () => setStep('screen', s.id),
                      })
                    )}
                    {renderNoneCard(screenId === NONE, () => setStep('screen', NONE), 'No screens')}
                  </div>
                ) : (
                  <p className={styles.scopeNote}>No mount selected, so no screens are included in the total.</p>
                )}
              </Step>

              <Step n={10} title="Keyboard" caption="One wireless keyboard with a trackpad is all a sim rig needs. It drops straight onto the Keyboard & Mouse Tray.">
                <div className={styles.grid}>
                  {KEYBOARD_OPTIONS.map((k) =>
                    renderPlainCard({
                      id: k.id,
                      name: k.name,
                      spec: k.spec,
                      cents: thirdPartyCents(k.priceEur, currency),
                      suffix: ' est.',
                      selected: keyboardId === k.id,
                      onSelect: () => setStep('keyboard', k.id),
                    })
                  )}
                  {renderNoneCard(keyboardId === NONE, () => setStep('keyboard', NONE), 'No keyboard')}
                </div>
              </Step>

              <Step n={11} title="Shipping" caption="Rigs ship as freight from our EU warehouse. Rates below are placeholders while we finalise the real ones.">
                <div className={styles.grid}>
                  {SHIPPING_OPTIONS.map((x) =>
                    renderPlainCard({
                      id: x.id,
                      name: x.name,
                      spec: x.spec,
                      cents: thirdPartyCents(x.priceEur, currency),
                      note: x.placeholder ? 'Placeholder rate' : null,
                      selected: shippingId === x.id,
                      onSelect: () => setStep('shipping', x.id),
                    })
                  )}
                </div>
              </Step>

              <Step n={12} title="Assembly" caption="Build it yourself, or have it arrive ready to drive.">
                <div className={styles.grid}>
                  {ASSEMBLY_OPTIONS.map((x) =>
                    renderPlainCard({
                      id: x.id,
                      name: x.name,
                      spec: x.spec,
                      cents: thirdPartyCents(x.priceEur, currency),
                      note: x.placeholder ? 'Placeholder price' : null,
                      selected: assemblyId === x.id,
                      onSelect: () => setStep('assembly', x.id),
                    })
                  )}
                </div>
              </Step>

              {plans.length > 0 && (
                <Step n={13} title="Get faster with VRS Academy" caption="Telemetry, data packs and analysis tools. Billed separately from the rig, so it sits outside the build total.">
                  <div className={styles.grid}>
                    {plans.map((p) => {
                      const monthly = p.price && p.price.monthly ? p.price.monthly.priceCents : 0
                      return renderPlainCard({
                        id: p.code,
                        name: p.name,
                        spec: SUBSCRIPTION_BLURB[p.code] || p.description,
                        cents: monthly,
                        suffix: monthly ? ' / month' : null,
                        note: p.price && p.price.yearly ? `or ${price(p.price.yearly.priceCents, p.price.yearly.currency)} a year` : null,
                        selected: planCode === p.code,
                        onSelect: () => setStep('plan', p.code),
                      })
                    })}
                  </div>
                </Step>
              )}
              </>)}
            </div>

            <aside className={styles.summary}>
              <div className={styles.summaryInner}>
                <div className={styles.preview}>
                  {heroImage && (
                    <Img image={heroImage} sizes="(max-width: 900px) 100vw, 360px" layout="responsive" objectFit="contain" alt={offerName(heroOffer || {})} />
                  )}
                </div>
                <h2 className={styles.summaryTitle}>Your build</h2>
                <ul className={styles.sheet}>
                  {lines.map((l) => (
                    <li key={`${l.label}-${l.id}`}>
                      <span className={styles.sheetLabel}>{l.label}</span>
                      <span className={styles.sheetName}>{l.name}</span>
                      <span className={styles.sheetPrice}>
                        {l.wasCents ? <s className={styles.sheetWas}>{price(l.wasCents, currency)}</s> : null}
                        {price(l.cents, currency)}
                      </span>
                    </li>
                  ))}
                </ul>
                <dl className={styles.subtotals}>
                  <div>
                    <dt>VRS hardware</dt>
                    <dd>{price(vrsTotal, currency)}</dd>
                  </div>
                  <div>
                    <dt>PC, screens, keyboard</dt>
                    <dd>{price(gearTotal, currency)}</dd>
                  </div>
                  <div>
                    <dt>Shipping and assembly</dt>
                    <dd>{price(serviceTotal, currency)}</dd>
                  </div>
                </dl>
                <div className={styles.total}>
                  <span>Total</span>
                  <strong>{price(total, currency)}</strong>
                </div>
                {plan && (
                  <div className={styles.recurring}>
                    <span>VRS Academy — {plan.name}</span>
                    <strong>{planMonthlyCents ? `${price(planMonthlyCents, currency)}/month` : 'Free'}</strong>
                  </div>
                )}
                <p className={styles.totalNote}>
                  Excluding tax. Shipping and assembly prices are placeholders. PC, screen and keyboard prices are
                  indicative retail ({THIRD_PARTY_SOURCE}){currency !== 'EUR' ? ', converted from EUR' : ''} and are not a
                  VRS quote. The VRS Academy plan is billed monthly and is not part of the total.
                </p>
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  )
}

export default TurnkeyConfigurator
